import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useSupabaseAuth } from '@/contexts/SupabaseAuthContext'
import { apiClient } from '@/lib/api-client'
import { useWorkspaceConfig } from '@/hooks/useWorkspaceConfig'
import { useTheme } from '@/contexts/ThemeContext'
import { LanguageToggle } from './LanguageToggle'
import { DesktopNav } from './DesktopNav'
import { UserDropdown } from './UserDropdown'
import { MobileMenuPanel } from './MobileMenuPanel'
import { LayoutDashboard, Grid3X3, Package, Palette, Shield, Menu, X, Sun, Moon } from 'lucide-react'
import { cn, formatCredits } from '@/lib/utils'

export interface NavItem {
  path: string
  label: string
  icon: React.ComponentType<{ className?: string }>
  danger?: boolean
  badge?: string
}

/** V2 Navbar - Swiss Precision aesthetic. WCAG 2.1 AA + BFSG konform */
export function NavbarV2() {
  const { t } = useTranslation()
  const { user, isAuthenticated, logout: supabaseLogout } = useSupabaseAuth()
  const { theme, toggleTheme } = useTheme()
  const { config } = useWorkspaceConfig()
  const location = useLocation()
  const [mobileOpen, setMobileOpen] = useState(false)
  const credits = config?.user_credits ?? 0
  const creditsDisplay = formatCredits(credits)
  const logoPath = theme === 'dark' ? '/images/logo-dark.svg' : '/images/logo-white.svg'

  const handleLogout = async () => {
    try { await Promise.all([apiClient.post('/api/v2/auth/logout'), supabaseLogout()]) }
    catch (error) { console.error("Logout failed", error) }
    finally { window.location.href = '/v2/login' }
  }

  if (!isAuthenticated || !user) return null

  const isActive = (path: string) => location.pathname.startsWith(path)
  const navItems: NavItem[] = [
    { path: '/v2/dashboard', label: t('nav.dashboard'), icon: LayoutDashboard },
    { path: '/v2/tools', label: t('nav.tools'), icon: Grid3X3 },
    { path: '/v2/products', label: t('nav.products'), icon: Package },
    { path: '/v2/templates', label: t('nav.templates'), icon: Palette },
  ]

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 h-16 bg-white dark:bg-neutral-900 border-b border-neutral-200 dark:border-neutral-800 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-full flex items-center justify-between">
        <Link to="/v2/dashboard" className="flex items-center gap-2.5 text-neutral-900 dark:text-neutral-100 font-semibold hover:opacity-80 transition-opacity group">
          <img src={logoPath} alt="GOKI Logo" className="h-[50px] w-auto transition-opacity group-hover:opacity-80"
            onError={(e) => { if (theme === 'dark') { e.currentTarget.src = '/images/logo-white.svg' } else { e.currentTarget.style.display = 'none' } }} />
        </Link>
        <DesktopNav navItems={navItems} isActive={isActive} />
        <div className="flex items-center gap-3">
          <Link
            to="/v2/credits"
            className="hidden lg:flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200/60 dark:border-emerald-800/40 hover:bg-emerald-100 dark:hover:bg-emerald-900/40 transition-colors"
            title={t('billing.availableBalance')}
          >
            {formatCredits(credits, true)}
          </Link>
          <button onClick={toggleTheme} className={cn("flex items-center justify-center w-10 h-10 rounded-lg transition-colors", "text-neutral-600 hover:text-neutral-900 hover:bg-neutral-100", "dark:text-neutral-400 dark:hover:text-neutral-100 dark:hover:bg-neutral-800", "focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2")}
            aria-label={theme === 'dark' ? t('nav.switchToLight') : t('nav.switchToDark')} title={theme === 'dark' ? t('nav.light') : t('nav.dark')}>
            {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </button>
          <LanguageToggle />
          <UserDropdown user={user} onLogout={handleLogout} />
          <button onClick={() => setMobileOpen(!mobileOpen)} className="lg:hidden flex items-center justify-center w-10 h-10 rounded-lg text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
            aria-label={mobileOpen ? t('nav.closeMenu') : t('nav.openMenu')}>
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      {user.is_admin && (
        <Link to="/v2/admin" className={cn("absolute top-3 right-[20px] flex items-center justify-center w-10 h-10 rounded-lg transition-colors", isActive('/v2/admin') ? "text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20" : "text-neutral-600 hover:text-red-600 hover:bg-red-50 dark:text-neutral-400 dark:hover:text-red-400 dark:hover:bg-red-900/20")} aria-label={t('nav.admin')} title={t('nav.admin')}>
          <Shield className="h-5 w-5" />
        </Link>
      )}
      <MobileMenuPanel navItems={navItems} mobileOpen={mobileOpen} onClose={() => setMobileOpen(false)} creditsDisplay={creditsDisplay} user={user} />
    </nav>
  )
}

export default NavbarV2
